/**
 * FollowUpPage
 * 
 * Página de rastreio de entregas com barra de progresso animada.
 * Monitora pedidos aprovados financeiramente até o recebimento.
 */

import React, { useState, useMemo, useRef, useEffect } from 'react';
import {
  Activity, Search, Filter, Truck, Package,
  FileText, CheckCircle2, Loader2,
  ShieldCheck, Info, X,
  PackageCheck, Timer,
  Eye, MessageSquare, Mail, Download,
  Upload, MapPin, CalendarDays,
  Navigation2, MoreVertical, LayoutDashboard, Cpu
} from 'lucide-react';
import { followUpService, purchaseService } from '../../../services/purchasesApi';
import { useToast } from '../../../hooks/useToast';
import { Toast } from '../../../components';
import ConfirmModal from '../../../components/ERP/ConfirmModal/ConfirmModal';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import './FollowUpPage.css';

const FollowUpPage = () => {
  const { toast, showToast, hideToast } = useToast();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStage, setFilterStage] = useState(null);
  const [isUpdating, setIsUpdating] = useState(null);
  const [showNFModal, setShowNFModal] = useState(null);
  const [showTransportModal, setShowTransportModal] = useState(null);
  const [showOrderModal, setShowOrderModal] = useState(null);
  const [orderDetails, setOrderDetails] = useState(null);
  const [loadingOrderDetails, setLoadingOrderDetails] = useState(false);
  const [activeMenu, setActiveMenu] = useState(null);
  const [tempNF, setTempNF] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [transportDates, setTransportDates] = useState({ collection: '', forecast: '' });
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showReceiptModal, setShowReceiptModal] = useState(null);
  const [createInvoiceEntryOnReceipt, setCreateInvoiceEntryOnReceipt] = useState(true);

  const menuRef = useRef(null);
  const fileInputRef = useRef(null);
  const pdfItemsRef = useRef(null);
  const pdfSummaryRef = useRef(null);
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [pdfOrderData, setPdfOrderData] = useState(null);
  const [engineLogs, setEngineLogs] = useState('Iniciando Motor...');

  const stages = [
    { key: 'finance_approved', label: 'Aprovado Fin.', icon: ShieldCheck, color: 'emerald', bg: 'emerald-light' },
    { key: 'sent', label: 'Enviado', icon: Package, color: 'blue', bg: 'blue-light' },
    { key: 'invoice_emitted', label: 'NF Emitida', icon: FileText, color: 'indigo', bg: 'indigo-light' },
    { key: 'transport', label: 'Transporte', icon: Truck, color: 'orange', bg: 'orange-light' },
    { key: 'received', label: 'Recebido', icon: CheckCircle2, color: 'emerald', bg: 'emerald-light' },
  ];

  // Carregar pedidos do backend
  useEffect(() => {
    const loadOrders = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await followUpService.getList();
        if (response.success) {
          // Mapear dados do backend para o formato esperado
          // O backend agora retorna um array direto, não mais { order, shipment }
          const mappedOrders = (response.data || []).map(item => {
            // Suportar tanto o formato antigo { order, shipment } quanto o novo formato direto
            const orderData = item.order || item;
            
            return {
              id: orderData.id || orderData.requisition_id,
              vendor: orderData.supplier_name || orderData.supplier?.name || 'Fornecedor não informado',
              title: orderData.title || 'Sem título',
              value: parseFloat(orderData.total_value || orderData.invoice_total || 0),
              date: orderData.created_at ? new Date(orderData.created_at).toLocaleDateString('pt-BR') : '',
              stage: mapStatusToStage(orderData.status),
              invoiceNumber: orderData.invoice_number,
              lastUpdate: formatLastUpdate(orderData.updated_at),
              collectionDate: orderData.collection_date,
              deliveryForecast: orderData.delivery_forecast || orderData.estimated_delivery_date
            };
          });
          setOrders(mappedOrders);
        } else {
          setError(response.message || 'Erro ao carregar pedidos');
          setOrders([]);
        }
      } catch (err) {
        console.error('Erro ao carregar pedidos:', err);
        setError('Erro ao carregar pedidos. Tente novamente.');
        setOrders([]);
      } finally {
        setLoading(false);
      }
    };

    loadOrders();
  }, []);

  // Mapear status do backend para stages da UI
  const mapStatusToStage = (status) => {
    const statusMap = {
      'approved': 'finance_approved',
      'sent': 'sent',
      'invoice_emitted': 'invoice_emitted',
      'in_transit': 'transport',
      'delivered': 'received',
      'received': 'received'
    };
    return statusMap[status] || 'finance_approved';
  };

  // Formatar última atualização
  const formatLastUpdate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Agora';
    if (diffMins < 60) return `${diffMins} min atrás`;
    if (diffHours < 24) return `${diffHours}h atrás`;
    if (diffDays === 1) return 'Ontem';
    return `${diffDays} dias atrás`;
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setActiveMenu(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleNextStage = async (id) => {
    const order = orders.find(o => o.id === id);
    if (!order) return;

    // Se está em "sent", abrir modal de NF
    if (order.stage === 'sent') {
      setShowNFModal(id);
      return;
    }

    // Se está em "invoice_emitted", abrir modal de transporte
    if (order.stage === 'invoice_emitted') {
      setShowTransportModal(id);
      return;
    }

    // Se está em "transport", abrir modal de confirmação de recebimento
    if (order.stage === 'transport') {
      setShowReceiptModal(id);
      setCreateInvoiceEntryOnReceipt(true); // Resetar para true por padrão
      return;
    }

    // Para outros estágios, avançar diretamente
    await performStageUpdate(id, order.stage);
  };

  const performStageUpdate = async (id, currentStage, createInvoiceEntry = true) => {
    setIsUpdating(id);
    try {
      const currentIndex = stages.findIndex(s => s.key === currentStage);
      const nextStage = stages[currentIndex + 1]?.key;
      
      if (nextStage) {
        // Mapear o próximo stage para status do backend
        // O status deve ser baseado no próximo stage, não no atual
        const statusMap = {
          'finance_approved': 'sent',
          'sent': 'invoice_emitted',
          'invoice_emitted': 'in_transit',
          'transport': 'received', // transport mapeia para received quando confirmar recebimento
          'received': 'received'     // received mapeia para received
        };
        const newStatus = statusMap[nextStage] || nextStage;

        // Preparar dados para atualização
        const updateData = { status: newStatus };
        
        // Se está confirmando recebimento (mudando de transport para received), incluir createInvoiceEntry
        if (currentStage === 'transport' && newStatus === 'received') {
          updateData.createInvoiceEntry = createInvoiceEntry;
        }

        const response = await followUpService.update(id, updateData);
        if (response.success) {
          setOrders(prev => prev.map(o => 
            o.id === id ? { ...o, stage: nextStage, lastUpdate: 'Agora' } : o
          ));
          showToast('Status atualizado com sucesso!', 'success');
        } else {
          showToast(response.message || 'Erro ao atualizar status', 'error');
        }
      }
    } catch (err) {
      console.error('Erro ao atualizar status:', err);
      const errorMessage = err.response?.data?.message || err.message || 'Erro ao atualizar status. Tente novamente.';
      showToast(errorMessage, 'error');
    } finally {
      setIsUpdating(null);
      setShowReceiptModal(null);
    }
  };

  const handleSaveNF = async () => {
    if (!showNFModal) return;
    
    try {
      const response = await followUpService.update(showNFModal, {
        invoice_number: tempNF || (selectedFile ? 'NF-UPLOADED' : 'PENDENTE'),
        status: 'invoice_emitted'
      });

      if (response.success) {
        setOrders(prev => prev.map(order =>
          order.id === showNFModal ? {
            ...order,
            stage: 'invoice_emitted',
            invoiceNumber: tempNF || (selectedFile ? 'NF-UPLOADED' : 'PENDENTE'),
            lastUpdate: 'Agora'
          } : order
        ));
        showToast('Nota fiscal vinculada com sucesso!', 'success');
        setShowNFModal(null);
        setTempNF('');
        setSelectedFile(null);
      } else {
        showToast(response.message || 'Erro ao vincular nota fiscal', 'error');
      }
    } catch (err) {
      console.error('Erro ao salvar NF:', err);
      showToast('Erro ao vincular nota fiscal. Tente novamente.', 'error');
    }
  };

  const handleSaveTransportInfo = async () => {
    if (!showTransportModal) return;
    
    try {
      const response = await followUpService.update(showTransportModal, {
        collection_date: transportDates.collection,
        delivery_forecast: transportDates.forecast,
        status: 'in_transit'
      });

      if (response.success) {
        setOrders(prev => prev.map(order =>
          order.id === showTransportModal ? {
            ...order,
            stage: 'transport',
            collectionDate: transportDates.collection,
            deliveryForecast: transportDates.forecast,
            lastUpdate: 'Agora'
          } : order
        ));
        showToast('Dados de transporte salvos com sucesso!', 'success');
        setShowTransportModal(null);
        setTransportDates({ collection: '', forecast: '' });
      } else {
        showToast(response.message || 'Erro ao salvar dados de transporte', 'error');
      }
    } catch (err) {
      console.error('Erro ao salvar transporte:', err);
      showToast('Erro ao salvar dados de transporte. Tente novamente.', 'error');
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  // Visualizar ordem de compra
  const handleViewOrder = async (orderId) => {
    try {
      setLoadingOrderDetails(true);
      setShowOrderModal(orderId);
      
      // Buscar detalhes da requisição
      const response = await purchaseService.getById(orderId);
      if (response.success) {
        setOrderDetails(response.data);
      } else {
        showToast(response.message || 'Erro ao carregar detalhes da ordem', 'error');
        setShowOrderModal(null);
      }
    } catch (err) {
      console.error('Erro ao carregar detalhes da ordem:', err);
      showToast('Erro ao carregar detalhes da ordem. Tente novamente.', 'error');
      setShowOrderModal(null);
    } finally {
      setLoadingOrderDetails(false);
    }
  };

  // Baixar PDF da ordem de compra
  const handleDownloadPDF = async (id) => {
    try {
      setActiveMenu(null);
      
      // Buscar dados da ordem
      const order = orders.find(o => o.id === id);
      if (!order) {
        showToast('Ordem não encontrada', 'error');
        return;
      }

      // Buscar detalhes completos da requisição
      setGeneratingPDF(true);
      setEngineLogs('Carregando dados da ordem...');
      
      const response = await purchaseService.getById(order.requisition_id || order.id);
      if (!response.success || !response.data) {
        showToast('Erro ao carregar dados da ordem', 'error');
        setGeneratingPDF(false);
        return;
      }

      setPdfOrderData(response.data);
      setEngineLogs('Preparando documento...');
      
      // Aguardar um pouco para garantir que os componentes foram renderizados
      await new Promise(r => setTimeout(r, 500));
      
      if (!pdfItemsRef.current || !pdfSummaryRef.current) {
        showToast('Erro ao gerar PDF. Tente novamente.', 'error');
        setGeneratingPDF(false);
        return;
      }

      setEngineLogs('Renderizando documento...');
      
      // Renderizar duas partes separadas: itens e resumo
      const [itemsCanvas, summaryCanvas] = await Promise.all([
        html2canvas(pdfItemsRef.current, {
          scale: 3,
          useCORS: true,
          backgroundColor: "#FFFFFF",
          logging: false,
          windowWidth: 1200,
          allowTaint: false,
          removeContainer: false,
        }),
        html2canvas(pdfSummaryRef.current, {
          scale: 3,
          useCORS: true,
          backgroundColor: "#FFFFFF",
          logging: false,
          windowWidth: 1200,
          allowTaint: false,
          removeContainer: false,
        })
      ]);
      
      setEngineLogs('Gerando PDF...');
      await new Promise(r => setTimeout(r, 400));
      
      const itemsImgData = itemsCanvas.toDataURL('image/jpeg', 0.98);
      const summaryImgData = summaryCanvas.toDataURL('image/jpeg', 0.98);
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      
      // Margens do PDF (uniformes para evitar páginas em branco)
      const margin = 10;
      const spacing = 5; // Espaçamento entre seções
      const contentWidth = pdfWidth - (margin * 2);
      
      // Calcular dimensões das imagens proporcionalmente
      const itemsImgHeight = (itemsCanvas.height * contentWidth) / itemsCanvas.width;
      const summaryImgHeight = (summaryCanvas.height * contentWidth) / summaryCanvas.width;
      
      // Altura disponível por página (mesma para todas as páginas)
      const availableHeightPerPage = pdfHeight - (margin * 2);
      
      setEngineLogs('Finalizando...');
      
      // === PARTE 1: Adicionar seção de itens ===
      let currentY = margin; // Posição Y atual
      let itemsHeightRemaining = itemsImgHeight;
      let itemsSourceY = 0; // Posição de origem na imagem dos itens
      
      // Primeira página: adicionar o máximo possível da seção de itens
      pdf.addImage(
        itemsImgData, 
        'JPEG', 
        margin, 
        currentY, 
        contentWidth, 
        itemsImgHeight, 
        undefined, 
        'FAST'
      );
      
      itemsHeightRemaining -= availableHeightPerPage;
      itemsSourceY = availableHeightPerPage;
      currentY = margin;
      
      // Adicionar páginas adicionais se os itens não couberem em uma página
      while (itemsHeightRemaining > 0) {
        pdf.addPage();
        currentY = margin;
        
        // Calcular deslocamento Y para mostrar a continuação correta da imagem
        const yOffset = -(itemsSourceY - margin);
        
        pdf.addImage(
          itemsImgData, 
          'JPEG', 
          margin, 
          currentY + yOffset, 
          contentWidth, 
          itemsImgHeight, 
          undefined, 
          'FAST'
        );
        
        itemsHeightRemaining -= availableHeightPerPage;
        itemsSourceY += availableHeightPerPage;
      }
      
      // Calcular quanto de altura foi usado na última página de itens
      const lastPageItemsHeight = itemsImgHeight % availableHeightPerPage || 
                                  (itemsImgHeight <= availableHeightPerPage ? itemsImgHeight : availableHeightPerPage);
      
      // === PARTE 2: Adicionar seção de resumo ===
      // Calcular espaço disponível na página atual (última página de itens)
      const spaceLeftInCurrentPage = availableHeightPerPage - lastPageItemsHeight;
      
      // Verificar se o resumo cabe na página atual com espaçamento adequado
      const summaryWithSpacing = summaryImgHeight + spacing;
      const needsNewPage = summaryWithSpacing > spaceLeftInCurrentPage;
      
      if (needsNewPage) {
        // Criar nova página para o resumo
        pdf.addPage();
        currentY = margin;
        
        // Verificar se o resumo cabe em uma página
        if (summaryImgHeight <= availableHeightPerPage) {
          // Cabe em uma página - adicionar normalmente
          pdf.addImage(
            summaryImgData, 
            'JPEG', 
            margin, 
            currentY, 
            contentWidth, 
            summaryImgHeight, 
            undefined, 
            'FAST'
          );
        } else {
          // Resumo precisa de múltiplas páginas (caso raro)
          let summaryHeightRemaining = summaryImgHeight;
          let summarySourceY = 0;
          
          while (summaryHeightRemaining > 0) {
            if (summarySourceY > 0) {
              pdf.addPage();
              currentY = margin;
            }
            
            const yOffset = -(summarySourceY - margin);
            pdf.addImage(
              summaryImgData, 
              'JPEG', 
              margin, 
              currentY + yOffset, 
              contentWidth, 
              summaryImgHeight, 
              undefined, 
              'FAST'
            );
            
            summaryHeightRemaining -= availableHeightPerPage;
            summarySourceY += availableHeightPerPage;
          }
        }
      } else {
        // Adicionar resumo na mesma página, abaixo dos itens
        currentY = margin + lastPageItemsHeight + spacing;
        pdf.addImage(
          summaryImgData, 
          'JPEG', 
          margin, 
          currentY, 
          contentWidth, 
          summaryImgHeight, 
          undefined, 
          'FAST'
        );
      }
      
      setEngineLogs('Concluído!');
      await new Promise(r => setTimeout(r, 400));
      
      const fileName = `Ordem_Compra_${order.id || order.requisition_id}_${new Date().getFullYear()}_${Date.now()}.pdf`;
      pdf.save(fileName);
      
      showToast('PDF gerado com sucesso!', 'success');
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      showToast('Erro ao gerar PDF. Tente novamente.', 'error');
    } finally {
      setGeneratingPDF(false);
      setPdfOrderData(null);
    }
  };

  // Enviar ordem de compra via WhatsApp
  const handleSendWhatsApp = async (orderId) => {
    try {
      // Buscar detalhes da requisição se ainda não tiver
      let requisitionData = orderDetails;
      if (!requisitionData || requisitionData.id !== orderId) {
        const response = await purchaseService.getById(orderId);
        if (response.success) {
          requisitionData = response.data;
        } else {
          showToast('Erro ao carregar dados para envio', 'error');
          return;
        }
      }

      // Formatar mensagem
      const formatWhatsAppMessage = (data) => {
        let message = `*ORDEM DE COMPRA*\n\n`;
        message += `*ID:* ${data.id || 'N/A'}\n`;
        message += `*Título:* ${data.title || 'Sem título'}\n`;
        message += `*Status:* ${data.status || 'N/A'}\n\n`;
        
        if (data.supplier) {
          message += `*FORNECEDOR*\n`;
          message += `Nome: ${data.supplier.name || 'N/A'}\n`;
          if (data.supplier.cpf_cnpj) {
            message += `CNPJ/CPF: ${data.supplier.cpf_cnpj}\n`;
          }
          message += `\n`;
        }

        message += `*VALORES*\n`;
        const totalValue = parseFloat(data.invoice_total || data.total_value || 0);
        message += `Valor Total: R$ ${totalValue.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}\n`;
        if (data.invoice_number) {
          message += `Nota Fiscal: ${data.invoice_number}\n`;
        }
        message += `\n`;

        if (data.items && data.items.length > 0) {
          message += `*ITENS DA REQUISIÇÃO*\n`;
          data.items.forEach((item, idx) => {
            message += `${idx + 1}. ${item.product_name || 'Produto não informado'}\n`;
            message += `   Qtd: ${item.requested_qty || 0}\n`;
            if (item.unit_price) {
              message += `   Valor Unit: R$ ${parseFloat(item.unit_price).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}\n`;
            }
            if (item.item_total) {
              message += `   Total: R$ ${parseFloat(item.item_total).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}\n`;
            }
            message += `\n`;
          });
        }

        if (data.payment_method) {
          message += `*FORMA DE PAGAMENTO*\n`;
          message += `Método: ${data.payment_method === 'cash' ? 'À Vista' : data.payment_method === 'installments' ? 'Parcelado' : data.payment_method}\n`;
          if (data.payment_method === 'installments' && data.payment_installments) {
            message += `Parcelas: ${data.payment_installments}x\n`;
          }
        }

        return encodeURIComponent(message);
      };

      // Obter número do WhatsApp do fornecedor ou usar número padrão
      let phoneNumber = '';
      if (requisitionData.supplier) {
        // Prioridade: cellphone > phone > contact_phone
        const phone = requisitionData.supplier.cellphone || 
                     requisitionData.supplier.phone || 
                     requisitionData.supplier.contact_phone || 
                     '';
        
        // Limpar formatação do telefone (remover espaços, parênteses, traços, etc)
        const cleanPhone = phone.toString().replace(/\D/g, '');
        
        if (cleanPhone && cleanPhone.length >= 10) {
          // Se não começar com 55 (código do Brasil), adicionar
          phoneNumber = cleanPhone.startsWith('55') ? cleanPhone : `55${cleanPhone}`;
        }
      }

      // Se não tiver número do fornecedor, usar número padrão da empresa (pode ser configurado)
      if (!phoneNumber) {
        showToast('Número de WhatsApp do fornecedor não encontrado', 'warning');
        return;
      }

      // Criar link do WhatsApp
      const message = formatWhatsAppMessage(requisitionData);
      const whatsappUrl = `https://wa.me/${phoneNumber}?text=${message}`;
      
      // Abrir WhatsApp em nova aba
      window.open(whatsappUrl, '_blank');
      
      showToast('Abrindo WhatsApp...', 'success');
      setActiveMenu(null);
    } catch (err) {
      console.error('Erro ao enviar via WhatsApp:', err);
      showToast('Erro ao preparar envio via WhatsApp. Tente novamente.', 'error');
    }
  };

  // Lógica de progresso de transporte
  const getTransportProgress = (order) => {
    if (order.stage === 'received') return { percent: 100, color: 'emerald' };
    if (order.stage !== 'transport' || !order.collectionDate || !order.deliveryForecast) {
      const idx = stages.findIndex(s => s.key === order.stage);
      const basePercent = ((idx + 1) / stages.length) * 100;
      return { percent: basePercent, color: 'primary' };
    }

    const start = new Date(order.collectionDate).getTime();
    const end = new Date(order.deliveryForecast).getTime();
    const now = Date.now();

    if (now >= end) return { percent: 98, color: 'emerald' };
   
    const progress = ((now - start) / (end - start)) * 100;
    const finalPercent = Math.min(Math.max(progress, 10), 98);

    // Mudança de cor dinâmica
    let color = 'blue';
    if (finalPercent > 40 && finalPercent <= 80) color = 'amber';
    if (finalPercent > 80) color = 'emerald';

    return { percent: finalPercent, color };
  };

  const filteredOrders = useMemo(() => {
    return orders.filter(o => {
      const matchesSearch = 
        o.vendor.toLowerCase().includes(searchTerm.toLowerCase()) || 
        o.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (o.invoiceNumber && o.invoiceNumber.toLowerCase().includes(searchTerm.toLowerCase()));
      const matchesStage = filterStage ? o.stage === filterStage : true;
      return matchesSearch && matchesStage;
    });
  }, [orders, searchTerm, filterStage]);

  if (loading) {
    return (
      <div className="follow-up-page">
        <div className="follow-up-loading">
          <Loader2 size={48} className="spinner" />
          <p>Carregando pedidos...</p>
        </div>
      </div>
    );
  }

  if (error && orders.length === 0) {
    return (
      <div className="follow-up-page">
        <div className="follow-up-error">
          <Info size={48} />
          <p>{error}</p>
          <button onClick={() => window.location.reload()} className="btn-retry">
            Tentar Novamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="follow-up-page">
      {/* Header */}
      <div className="follow-up-header">
        <div>
          <h1 className="follow-up-title">
            Dashboard de <span className="follow-up-title-highlight">Acompanhamento</span>
          </h1>
          <p className="follow-up-subtitle">Logística Pós-Faturamento & Follow-up</p>
        </div>
        <div className="follow-up-header-actions">
          <button className="btn-logistics-view">
            <LayoutDashboard size={16} />
            Visão Geral Logística
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="follow-up-kpis">
        <div className="kpi-card kpi-transit">
          <div className="kpi-header">
            <div className="kpi-icon kpi-icon-blue">
              <Truck size={20} />
            </div>
            <span className="kpi-badge kpi-badge-blue">Fluxo Ativo</span>
          </div>
          <p className="kpi-label">Em Trânsito</p>
          <p className="kpi-value">{orders.filter(o => o.stage === 'transport').length}</p>
        </div>
       
        <div className="kpi-card kpi-invoice">
          <div className="kpi-header">
            <div className="kpi-icon kpi-icon-indigo">
              <FileText size={20} />
            </div>
            <span className="kpi-badge kpi-badge-indigo">Faturamento</span>
          </div>
          <p className="kpi-label">Aguardando NF</p>
          <p className="kpi-value">{orders.filter(o => o.stage === 'sent').length}</p>
        </div>

        <div className="kpi-card kpi-collection">
          <div className="kpi-header">
            <div className="kpi-icon kpi-icon-amber">
              <Timer size={20} />
            </div>
            <span className="kpi-badge kpi-badge-amber">Ação Necessária</span>
          </div>
          <p className="kpi-label">Aguardando Coleta</p>
          <p className="kpi-value">{orders.filter(o => o.stage === 'invoice_emitted').length}</p>
        </div>

        <div className="kpi-card kpi-received">
          <div className="kpi-header">
            <div className="kpi-icon kpi-icon-emerald">
              <PackageCheck size={20} />
            </div>
            <span className="kpi-badge kpi-badge-emerald">Concluídos</span>
          </div>
          <p className="kpi-label">Recebidos</p>
          <p className="kpi-value">{orders.filter(o => o.stage === 'received').length}</p>
        </div>
      </div>

      {/* Process Flow Strip */}
      <div className="follow-up-pipeline">
        <div className="pipeline-header">
          <h3 className="pipeline-title">
            <Activity size={16} className="pipeline-title-icon" />
            Pipeline de Acompanhamento
          </h3>
          {filterStage && (
            <button
              onClick={() => setFilterStage(null)}
              className="btn-clear-filter"
            >
              Limpar Filtro
            </button>
          )}
        </div>

        <div className="pipeline-stages">
          {stages.map((stage, idx) => {
            const count = orders.filter(o => o.stage === stage.key).length;
            const isActive = filterStage === stage.key;
            const StageIcon = stage.icon;

            return (
              <div
                key={stage.key}
                onClick={() => setFilterStage(stage.key)}
                className={`pipeline-stage ${isActive ? 'pipeline-stage-active' : ''}`}
              >
                <div className={`pipeline-stage-icon pipeline-stage-icon-${stage.color} ${isActive ? 'pipeline-stage-icon-active' : ''}`}>
                  <StageIcon size={24} />
                  <div className={`pipeline-stage-badge ${count > 0 ? 'pipeline-stage-badge-active' : ''}`}>
                    {count}
                  </div>
                </div>
                <p className={`pipeline-stage-label ${isActive ? 'pipeline-stage-label-active' : ''}`}>
                  {stage.label}
                </p>
                {idx < stages.length - 1 && (
                  <div className="pipeline-stage-connector" />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Main List Container */}
      <div className="follow-up-list-container">
        <div className="follow-up-list-header">
          <div className="follow-up-search-wrapper">
            <Search className={`follow-up-search-icon ${searchTerm ? 'follow-up-search-icon-active' : ''}`} size={16} />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Pesquisar por fornecedor, pedido ou NF..."
              className="follow-up-search-input"
            />
          </div>
          <div className="follow-up-list-actions">
            <div className="follow-up-count-badge">
              Mostrando <span className="follow-up-count-number">{filteredOrders.length}</span> Pedidos
            </div>
            <button className="btn-filter">
              <Filter size={18} />
            </button>
          </div>
        </div>

        <div className="follow-up-table-wrapper">
          <table className="follow-up-table">
            <thead>
              <tr>
                <th>ID Pedido / Fornecedor</th>
                <th>Informações Adicionais</th>
                <th className="text-center">Progresso & Fluxo</th>
                <th>Última Att.</th>
                <th className="text-right">Ação Sugerida</th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.length > 0 ? filteredOrders.map((order) => {
                const currentStageIndex = stages.findIndex(s => s.key === order.stage);
                const config = stages[currentStageIndex];
                const progressInfo = getTransportProgress(order);
               
                return (
                  <tr key={order.id} className="follow-up-table-row">
                    <td>
                      <div className="follow-up-order-info">
                        <div className="follow-up-order-details">
                          <span className="follow-up-order-id">{order.id}</span>
                          <span className="follow-up-order-vendor">{order.vendor}</span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="follow-up-order-meta">
                        <p className="follow-up-order-title">{order.title}</p>
                        <div className="follow-up-order-value-row">
                          <span className="follow-up-order-value">
                            R$ {order.value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                          {order.invoiceNumber && (
                            <span className="follow-up-invoice-badge">
                              <FileText size={10} />
                              {order.invoiceNumber}
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="follow-up-progress-wrapper">
                        <div className="follow-up-progress-bar">
                          <div
                            className={`follow-up-progress-fill follow-up-progress-fill-${progressInfo.color}`}
                            style={{ width: `${progressInfo.percent}%` }}
                          >
                            <div className="follow-up-progress-shimmer" />
                            {order.stage === 'transport' && (
                              <div className="follow-up-progress-truck">
                                <div className={`follow-up-truck-icon follow-up-truck-icon-${progressInfo.color}`}>
                                  <Truck size={12} />
                                </div>
                              </div>
                            )}
                          </div>
                          <div className="follow-up-progress-markers">
                            <div className="follow-up-progress-marker" />
                            <div className="follow-up-progress-marker" />
                          </div>
                        </div>
                        <span className={`follow-up-progress-label follow-up-progress-label-${config?.color || 'primary'}`}>
                          {order.stage === 'transport' ? (
                            <>
                              <Navigation2 size={10} className="follow-up-progress-nav-icon" />
                              {progressInfo.percent.toFixed(0)}% Percurso Concluído
                            </>
                          ) : (
                            config?.label || 'N/A'
                          )}
                        </span>
                      </div>
                    </td>
                    <td>
                      <div className="follow-up-update-info">
                        <span className="follow-up-update-time">{order.lastUpdate}</span>
                        <span className="follow-up-update-date">{order.date}</span>
                      </div>
                    </td>
                    <td className="text-right">
                      <div className="follow-up-actions">
                        {order.stage !== 'received' && (
                          <button
                            onClick={() => handleNextStage(order.id)}
                            disabled={isUpdating === order.id}
                            className="btn-next-stage"
                          >
                            {isUpdating === order.id ? (
                              <Loader2 size={12} className="spinner" />
                            ) : (
                              <>
                                {order.stage === 'finance_approved' && 'Marcar como Enviado'}
                                {order.stage === 'sent' && 'Informar Nota Fiscal'}
                                {order.stage === 'invoice_emitted' && 'Confirmar Coleta'}
                                {order.stage === 'transport' && 'Confirmar Recebimento'}
                              </>
                            )}
                          </button>
                        )}
                       
                        <div className="follow-up-menu-wrapper">
                          <button
                            onClick={() => setActiveMenu(activeMenu === order.id ? null : order.id)}
                            className={`btn-menu ${activeMenu === order.id ? 'btn-menu-active' : ''} ${order.stage === 'received' ? 'btn-menu-small' : ''}`}
                          >
                            <MoreVertical size={order.stage === 'received' ? 25 : 60} />
                          </button>

                          {activeMenu === order.id && (
                            <div ref={menuRef} className="follow-up-menu">
                              {order.stage === 'finance_approved' && (
                                <>
                                  <button 
                                    className="follow-up-menu-item follow-up-menu-item-primary"
                                    onClick={() => {
                                      setActiveMenu(null);
                                      handleViewOrder(order.id);
                                    }}
                                  >
                                    <Eye size={14} /> Visualizar Ordem Compra
                                  </button>
                                  <button 
                                    className="follow-up-menu-item follow-up-menu-item-emerald"
                                    onClick={() => {
                                      setActiveMenu(null);
                                      handleSendWhatsApp(order.id);
                                    }}
                                  >
                                    <MessageSquare size={14} /> Enviar via Whats
                                  </button>
                                  <button className="follow-up-menu-item follow-up-menu-item-blue">
                                    <Mail size={14} /> Enviar via E-mail
                                  </button>
                                  <button 
                                    className="follow-up-menu-item"
                                    onClick={() => handleDownloadPDF(order.id)}
                                  >
                                    <Download size={14} /> Baixar PDF Ordem de Compra
                                  </button>
                                  <div className="follow-up-menu-divider" />
                                </>
                              )}

                              <button className="follow-up-menu-item">
                                <Info size={14} className="follow-up-menu-icon-blue" /> Detalhes Logísticos
                              </button>
                             
                              {order.invoiceNumber && (
                                <button className="follow-up-menu-item follow-up-menu-item-indigo">
                                  <Download size={14} /> Baixar XML da Nota
                                </button>
                              )}
                             
                              <div className="follow-up-menu-divider" />
                              <button className="follow-up-menu-item follow-up-menu-item-danger">
                                <X size={14} /> Suspender Entrega
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                );
              }) : (
                <tr>
                  <td colSpan={5} className="follow-up-empty-state">
                    <div className="follow-up-empty-content">
                      <Search size={48} className="follow-up-empty-icon" />
                      <p>Nenhum pedido encontrado nesta etapa</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal de Nota Fiscal */}
      {showNFModal && (
        <div className="follow-up-modal-overlay" onClick={() => { setShowNFModal(null); setSelectedFile(null); }}>
          <div className="follow-up-modal" onClick={(e) => e.stopPropagation()}>
            <div className="follow-up-modal-header">
              <div className="follow-up-modal-header-content">
                <div className="follow-up-modal-icon follow-up-modal-icon-indigo">
                  <FileText size={20} />
                </div>
                <h3 className="follow-up-modal-title">Vincular Nota</h3>
              </div>
              <button 
                onClick={() => { setShowNFModal(null); setSelectedFile(null); }} 
                className="btn-modal-close"
              >
                <X size={24} />
              </button>
            </div>
           
            <div className="follow-up-modal-body">
              <div className="follow-up-modal-info">
                <Info size={20} className="follow-up-modal-info-icon" />
                <p>
                  O upload do documento permite que o Gemini valide impostos e quantidades automaticamente.
                </p>
              </div>

              <div
                onClick={() => fileInputRef.current?.click()}
                className={`follow-up-file-upload ${selectedFile ? 'follow-up-file-upload-active' : ''}`}
              >
                <input 
                  type="file" 
                  ref={fileInputRef} 
                  className="hidden" 
                  onChange={handleFileChange} 
                  accept=".pdf,.xml" 
                />
                {selectedFile ? (
                  <>
                    <div className="follow-up-file-upload-success">
                      <CheckCircle2 size={24} />
                    </div>
                    <div className="follow-up-file-upload-info">
                      <p className="follow-up-file-upload-name">{selectedFile.name}</p>
                      <button 
                        onClick={(e) => { e.stopPropagation(); setSelectedFile(null); }} 
                        className="follow-up-file-upload-remove"
                      >
                        Remover Arquivo
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="follow-up-file-upload-placeholder">
                      <Upload size={24} />
                    </div>
                    <div className="follow-up-file-upload-info">
                      <p className="follow-up-file-upload-label">Anexar PDF ou XML</p>
                      <p className="follow-up-file-upload-hint">Clique ou arraste o arquivo</p>
                    </div>
                  </>
                )}
              </div>

              <div className="follow-up-modal-field">
                <label className="follow-up-modal-label">Número da Nota Fiscal</label>
                <input
                  type="text"
                  placeholder="Ex: 000.123.456"
                  className="follow-up-modal-input"
                  value={tempNF}
                  onChange={(e) => setTempNF(e.target.value)}
                />
              </div>

              <button 
                onClick={handleSaveNF} 
                disabled={!tempNF && !selectedFile} 
                className="btn-modal-save"
              >
                Vincular NF e Avançar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Transporte */}
      {showTransportModal && (
        <div className="follow-up-modal-overlay" onClick={() => setShowTransportModal(null)}>
          <div className="follow-up-modal" onClick={(e) => e.stopPropagation()}>
            <div className="follow-up-modal-header">
              <div className="follow-up-modal-header-content">
                <div className="follow-up-modal-icon follow-up-modal-icon-orange">
                  <Truck size={20} />
                </div>
                <h3 className="follow-up-modal-title">Dados de Transporte</h3>
              </div>
              <button onClick={() => setShowTransportModal(null)} className="btn-modal-close">
                <X size={24} />
              </button>
            </div>
           
            <div className="follow-up-modal-body">
              <div className="follow-up-modal-info follow-up-modal-info-orange">
                <Info size={20} className="follow-up-modal-info-icon" />
                <p>
                  Ao informar as datas, o sistema inicia o rastreio automático e notifica o solicitante sobre a saída da carga.
                </p>
              </div>

              <div className="follow-up-modal-fields">
                <div className="follow-up-modal-field">
                  <label className="follow-up-modal-label">
                    <MapPin size={12} /> Data da Coleta
                  </label>
                  <input
                    type="date"
                    className="follow-up-modal-input"
                    value={transportDates.collection}
                    onChange={(e) => setTransportDates({...transportDates, collection: e.target.value})}
                  />
                </div>

                <div className="follow-up-modal-field">
                  <label className="follow-up-modal-label">
                    <CalendarDays size={12} /> Previsão de Entrega
                  </label>
                  <input
                    type="date"
                    className="follow-up-modal-input"
                    value={transportDates.forecast}
                    onChange={(e) => setTransportDates({...transportDates, forecast: e.target.value})}
                  />
                </div>
              </div>

              <button
                onClick={handleSaveTransportInfo}
                disabled={!transportDates.collection || !transportDates.forecast}
                className="btn-modal-save"
              >
                Confirmar e Despachar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Visualização da Ordem de Compra */}
      {showOrderModal && (
        <div className="follow-up-modal-overlay" onClick={() => { setShowOrderModal(null); setOrderDetails(null); }}>
          <div className="follow-up-modal follow-up-modal-large" onClick={(e) => e.stopPropagation()}>
            <div className="follow-up-modal-header">
              <div className="follow-up-modal-header-content">
                <div className="follow-up-modal-icon follow-up-modal-icon-primary">
                  <FileText size={20} />
                </div>
                <h3 className="follow-up-modal-title">Ordem de Compra</h3>
              </div>
              <button 
                onClick={() => { setShowOrderModal(null); setOrderDetails(null); }} 
                className="btn-modal-close"
              >
                <X size={24} />
              </button>
            </div>
           
            <div className="follow-up-modal-body">
              {loadingOrderDetails ? (
                <div className="follow-up-order-loading">
                  <Loader2 size={32} className="spinner" />
                  <p>Carregando detalhes da ordem...</p>
                </div>
              ) : orderDetails ? (
                <div className="follow-up-order-details">
                  {/* Informações Gerais */}
                  <div className="follow-up-order-section">
                    <h4 className="follow-up-order-section-title">Informações Gerais</h4>
                    <div className="follow-up-order-info-grid">
                      <div className="follow-up-order-info-item">
                        <span className="follow-up-order-info-label">ID da Requisição</span>
                        <span className="follow-up-order-info-value">{orderDetails.id || 'N/A'}</span>
                      </div>
                      <div className="follow-up-order-info-item">
                        <span className="follow-up-order-info-label">Título</span>
                        <span className="follow-up-order-info-value">{orderDetails.title || 'Sem título'}</span>
                      </div>
                      <div className="follow-up-order-info-item">
                        <span className="follow-up-order-info-label">Status</span>
                        <span className="follow-up-order-info-value">{orderDetails.status || 'N/A'}</span>
                      </div>
                      <div className="follow-up-order-info-item">
                        <span className="follow-up-order-info-label">Prioridade</span>
                        <span className="follow-up-order-info-value">{orderDetails.priority || 'Normal'}</span>
                      </div>
                      <div className="follow-up-order-info-item">
                        <span className="follow-up-order-info-label">Solicitante</span>
                        <span className="follow-up-order-info-value">
                          {orderDetails.requester_name || orderDetails.requester_email || 'N/A'}
                        </span>
                      </div>
                      <div className="follow-up-order-info-item">
                        <span className="follow-up-order-info-label">Data de Criação</span>
                        <span className="follow-up-order-info-value">
                          {orderDetails.created_at 
                            ? new Date(orderDetails.created_at).toLocaleDateString('pt-BR', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })
                            : 'N/A'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Fornecedor */}
                  {orderDetails.supplier_id && (
                    <div className="follow-up-order-section">
                      <h4 className="follow-up-order-section-title">Fornecedor</h4>
                      <div className="follow-up-order-info-grid">
                        <div className="follow-up-order-info-item">
                          <span className="follow-up-order-info-label">Nome</span>
                          <span className="follow-up-order-info-value">
                            {orderDetails.supplier?.name || 'N/A'}
                          </span>
                        </div>
                        <div className="follow-up-order-info-item">
                          <span className="follow-up-order-info-label">CNPJ/CPF</span>
                          <span className="follow-up-order-info-value">
                            {orderDetails.supplier?.cpf_cnpj || 'N/A'}
                          </span>
                        </div>
                        <div className="follow-up-order-info-item">
                          <span className="follow-up-order-info-label">E-mail</span>
                          <span className="follow-up-order-info-value">
                            {orderDetails.supplier?.email || 'N/A'}
                          </span>
                        </div>
                        <div className="follow-up-order-info-item">
                          <span className="follow-up-order-info-label">Telefone</span>
                          <span className="follow-up-order-info-value">
                            {orderDetails.supplier?.phone || orderDetails.supplier?.cellphone || 'N/A'}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Valores */}
                  <div className="follow-up-order-section">
                    <h4 className="follow-up-order-section-title">Valores</h4>
                    <div className="follow-up-order-info-grid">
                      <div className="follow-up-order-info-item">
                        <span className="follow-up-order-info-label">Valor Total</span>
                        <span className="follow-up-order-info-value follow-up-order-info-value-highlight">
                          R$ {parseFloat(orderDetails.invoice_total || orderDetails.total_value || 0)
                            .toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </div>
                      {orderDetails.invoice_number && (
                        <div className="follow-up-order-info-item">
                          <span className="follow-up-order-info-label">Número da Nota Fiscal</span>
                          <span className="follow-up-order-info-value">{orderDetails.invoice_number}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Itens da Requisição */}
                  {orderDetails.items && orderDetails.items.length > 0 && (
                    <div className="follow-up-order-section">
                      <h4 className="follow-up-order-section-title">Itens da Requisição</h4>
                      <div className="follow-up-order-items-table">
                        <table>
                          <thead>
                            <tr>
                              <th>Produto</th>
                              <th>Quantidade</th>
                              <th>Valor Unit.</th>
                              <th>Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {orderDetails.items.map((item, idx) => (
                              <tr key={idx}>
                                <td>{item.product_name || 'Produto não informado'}</td>
                                <td>{item.requested_qty || 0}</td>
                                <td>
                                  {item.unit_price 
                                    ? `R$ ${parseFloat(item.unit_price).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                    : 'N/A'}
                                </td>
                                <td>
                                  {item.item_total 
                                    ? `R$ ${parseFloat(item.item_total).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                    : 'N/A'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Parcelas (se houver) */}
                  {orderDetails.payments && orderDetails.payments.length > 0 && (
                    <div className="follow-up-order-section">
                      <h4 className="follow-up-order-section-title">Forma de Pagamento</h4>
                      <div className="follow-up-order-info-grid">
                        <div className="follow-up-order-info-item">
                          <span className="follow-up-order-info-label">Método</span>
                          <span className="follow-up-order-info-value">
                            {orderDetails.payment_method === 'cash' ? 'À Vista' : 
                             orderDetails.payment_method === 'installments' ? 'Parcelado' : 
                             orderDetails.payment_method || 'N/A'}
                          </span>
                        </div>
                        {orderDetails.payment_method === 'installments' && (
                          <div className="follow-up-order-info-item">
                            <span className="follow-up-order-info-label">Número de Parcelas</span>
                            <span className="follow-up-order-info-value">
                              {orderDetails.payment_installments || 'N/A'}
                            </span>
                          </div>
                        )}
                      </div>
                      {orderDetails.payment_method === 'installments' && (
                        <div className="follow-up-order-payments-list">
                          <h5 className="follow-up-order-payments-title">Parcelas</h5>
                          <div className="follow-up-order-payments">
                            {orderDetails.payments.map((payment, idx) => (
                              <div key={idx} className="follow-up-order-payment-item">
                                <span className="follow-up-order-payment-number">Parcela {idx + 1}</span>
                                <span className="follow-up-order-payment-value">
                                  R$ {parseFloat(payment.amount || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </span>
                                <span className="follow-up-order-payment-date">
                                  {payment.due_date 
                                    ? new Date(payment.due_date).toLocaleDateString('pt-BR')
                                    : 'N/A'}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="follow-up-order-error">
                  <Info size={32} />
                  <p>Não foi possível carregar os detalhes da ordem.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Overlay de Geração de PDF */}
      {generatingPDF && (
        <div className="follow-up-pdf-overlay">
          <div className="follow-up-pdf-overlay-content">
            <div className="follow-up-pdf-icon-wrapper">
              <Cpu size={32} className="spinner" />
              <div className="follow-up-pdf-scan" />
            </div>
            <div>
              <h4 className="follow-up-pdf-title">Gerando PDF</h4>
              <p className="follow-up-pdf-subtitle">Exportando Ordem de Compra</p>
            </div>
            <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div className="follow-up-pdf-log">
                <p className="follow-up-pdf-log-text">{engineLogs}</p>
              </div>
              <div className="follow-up-pdf-progress-bar">
                <div className="follow-up-pdf-progress-fill" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Área oculta para geração de PDF */}
      {pdfOrderData && (
        <>
          {/* Container principal com cabeçalho e itens */}
          <div ref={pdfItemsRef} className="order-pdf-container" style={{ position: 'absolute', left: '-9999px', top: 0, width: '210mm', backgroundColor: '#FFFFFF' }}>
            {/* Cabeçalho */}
            <div className="order-pdf-header">
              <div className="order-pdf-header-decoration" />
              <div className="order-pdf-header-content">
                <div className="order-pdf-logo">
                  <div className="order-pdf-logo-icon">PM</div>
                  <span className="order-pdf-logo-text">med</span>
                </div>
                <h1 className="order-pdf-title">Ordem de Compra</h1>
                <p className="order-pdf-doc-number">
                  Doc: #OC-{new Date().getFullYear()}-{String(pdfOrderData.id || Date.now()).slice(-4)}
                </p>
              </div>
              <div className="order-pdf-header-info">
                <div className="order-pdf-info-group">
                  <p className="order-pdf-info-label">Data de Emissão</p>
                  <p className="order-pdf-info-value">
                    {new Date().toLocaleDateString('pt-BR', { day: 'numeric', month: 'long', year: 'numeric' })}
                  </p>
                </div>
                <div className="order-pdf-info-group">
                  <p className="order-pdf-info-label">Status</p>
                  <p className="order-pdf-info-value primary">
                    {pdfOrderData.status === 'approved' ? 'Aprovado' :
                     pdfOrderData.status === 'sent' ? 'Enviado' :
                     pdfOrderData.status === 'invoice_emitted' ? 'NF Emitida' :
                     pdfOrderData.status === 'in_transit' ? 'Em Trânsito' :
                     pdfOrderData.status === 'received' ? 'Recebido' :
                     pdfOrderData.status || 'N/A'}
                  </p>
                </div>
              </div>
            </div>

            {/* Corpo do Documento - Apenas até os itens */}
            <div className="order-pdf-body">

            {/* Informações Gerais e Fornecedor */}
            <div className="order-pdf-parties">
              <div className="order-pdf-party">
                <h4 className="order-pdf-party-title">Dados da Requisição</h4>
                <div>
                  <p className="order-pdf-party-name">{pdfOrderData.title || 'Sem título'}</p>
                  <p className="order-pdf-party-detail">ID: #{pdfOrderData.id || 'N/A'}</p>
                  <p className="order-pdf-party-cnpj">
                    Solicitante: {pdfOrderData.requester_name || pdfOrderData.requester_email || 'N/A'}
                  </p>
                  <p className="order-pdf-party-cnpj">
                    Prioridade: {pdfOrderData.priority === 'urgent' ? 'Urgente' :
                                pdfOrderData.priority === 'critical' ? 'Crítica' :
                                pdfOrderData.priority === 'low' ? 'Baixa' : 'Normal'}
                  </p>
                  <p className="order-pdf-party-cnpj">
                    Criado em: {pdfOrderData.created_at 
                      ? new Date(pdfOrderData.created_at).toLocaleDateString('pt-BR', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })
                      : 'N/A'}
                  </p>
                </div>
              </div>
              
              {pdfOrderData.supplier && (
                <div className="order-pdf-party" style={{ textAlign: 'right' }}>
                  <h4 className="order-pdf-party-title">Fornecedor</h4>
                  <div>
                    <p className="order-pdf-party-name">{pdfOrderData.supplier.name || 'N/A'}</p>
                    <p className="order-pdf-party-detail">
                      {pdfOrderData.supplier.cpf_cnpj ? `CNPJ/CPF: ${pdfOrderData.supplier.cpf_cnpj}` : 'CNPJ/CPF não informado'}
                    </p>
                    {pdfOrderData.supplier.email && (
                      <p className="order-pdf-party-cnpj">{pdfOrderData.supplier.email}</p>
                    )}
                    {(pdfOrderData.supplier.phone || pdfOrderData.supplier.cellphone) && (
                      <p className="order-pdf-party-cnpj">
                        {pdfOrderData.supplier.phone || pdfOrderData.supplier.cellphone}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>


            {/* Tabela de Itens */}
            {pdfOrderData.items && pdfOrderData.items.length > 0 && (
              <div className="order-pdf-section">
                <table className="order-pdf-table">
                  <thead>
                    <tr>
                      <th>Descrição do Produto</th>
                      <th className="order-pdf-table-center">Qtd</th>
                      <th className="order-pdf-table-right">Unitário</th>
                      <th className="order-pdf-table-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pdfOrderData.items.map((item, idx) => (
                      <tr key={idx} className="order-pdf-table-row">
                        <td>
                          <p className="order-pdf-product-name">{item.product_name || 'Produto não informado'}</p>
                          <p className="order-pdf-product-note">Item #{idx + 1}</p>
                        </td>
                        <td className="order-pdf-table-center">{item.requested_qty || 0}</td>
                        <td className="order-pdf-table-right">
                          {item.unit_price 
                            ? `R$ ${parseFloat(item.unit_price).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                            : '-'}
                        </td>
                        <td className="order-pdf-table-right">
                          {item.item_total 
                            ? `R$ ${parseFloat(item.item_total).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                            : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Container separado para resumo e pagamento */}
        <div ref={pdfSummaryRef} className="order-pdf-container order-pdf-last-page" style={{ position: 'absolute', left: '-9999px', top: 0, width: '210mm', backgroundColor: '#FFFFFF', padding: '10mm' }}>
          {/* Resumo e Pagamento - Sempre na última página */}
          <div className="order-pdf-summary">
            <div className="order-pdf-notes">
              <h4 className="order-pdf-notes-title">
                <ShieldCheck size={14} /> Informações Importantes
              </h4>
              <ul className="order-pdf-notes-list">
                {pdfOrderData.invoice_number && (
                  <li>• Nota Fiscal: {pdfOrderData.invoice_number}</li>
                )}
                {pdfOrderData.collection_date && (
                  <li>• Data de Coleta: {new Date(pdfOrderData.collection_date).toLocaleDateString('pt-BR')}</li>
                )}
                {pdfOrderData.delivery_forecast && (
                  <li>• Previsão de Entrega: {new Date(pdfOrderData.delivery_forecast).toLocaleDateString('pt-BR')}</li>
                )}
                <li>• Produtos sujeitos à disponibilidade de estoque do fornecedor.</li>
                <li>• Prazo de entrega conforme acordado com o fornecedor.</li>
              </ul>
            </div>
            <div className="order-pdf-totals">
              <div>
                {pdfOrderData.payment_method && (
                  <div className="order-pdf-total-row">
                    <span>Forma de Pagamento</span>
                    <span className="order-pdf-total-row-value">
                      {pdfOrderData.payment_method === 'cash' ? 'À Vista' : 
                       pdfOrderData.payment_method === 'installments' ? `Parcelado (${pdfOrderData.payment_installments || 0}x)` : 
                       pdfOrderData.payment_method}
                    </span>
                  </div>
                )}
                {pdfOrderData.payments && pdfOrderData.payments.length > 0 && (
                  <div className="order-pdf-total-row">
                    <span>Total de Parcelas</span>
                    <span className="order-pdf-total-row-value">{pdfOrderData.payments.length}x</span>
                  </div>
                )}
              </div>
              <div className="order-pdf-final-total">
                <p className="order-pdf-final-total-label">Valor Total da Ordem</p>
                <p className="order-pdf-final-total-value">
                  R$ {parseFloat(pdfOrderData.invoice_total || pdfOrderData.total_value || 0)
                    .toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
            </div>
          </div>

          {/* Tabela de Parcelas */}
          {pdfOrderData.payments && pdfOrderData.payments.length > 0 && (
            <div className="order-pdf-section">
              <h4 className="order-pdf-section-title">Cronograma de Pagamento</h4>
              <table className="order-pdf-table order-pdf-payments-table">
                <thead>
                  <tr>
                    <th>Parcela</th>
                    <th>Vencimento</th>
                    <th className="order-pdf-table-right">Valor</th>
                  </tr>
                </thead>
                <tbody>
                  {pdfOrderData.payments.map((payment, idx) => (
                    <tr key={idx} className="order-pdf-table-row">
                      <td>{payment.installment_number || idx + 1}</td>
                      <td>
                        {payment.due_date 
                          ? new Date(payment.due_date).toLocaleDateString('pt-BR', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric'
                            })
                          : 'N/A'}
                      </td>
                      <td className="order-pdf-table-right">
                        R$ {parseFloat(payment.amount || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        </>
      )}

      {/* Modal de Confirmação de Recebimento */}
      <ConfirmModal
        isOpen={!!showReceiptModal}
        title="Confirmar Recebimento"
        message={
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <p>Deseja confirmar o recebimento desta mercadoria? Esta ação avançará o pedido para o estágio de recebido.</p>
            
            {/* Switch para criar nota de entrada automaticamente */}
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: '0.75rem',
              padding: '1rem',
              background: 'var(--bg-secondary)',
              borderRadius: '1rem',
              border: '1px solid var(--border-color)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', flex: 1 }}>
                  <label style={{ 
                    fontSize: '0.875rem', 
                    fontWeight: 700, 
                    color: 'var(--text-primary)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    <Package size={14} />
                    Criar Nota de Entrada Automaticamente
                  </label>
                  <p style={{ 
                    fontSize: '0.75rem', 
                    color: 'var(--text-secondary)',
                    margin: 0
                  }}>
                    {createInvoiceEntryOnReceipt 
                      ? 'Uma nota será criada automaticamente na página de Entrada de Mercadoria' 
                      : 'Nenhuma nota será criada. Você pode criar manualmente depois.'}
                  </p>
                </div>
                <div className="switch__container">
                  <input
                    id="switch-create-invoice-entry"
                    className="switch switch--shadow"
                    type="checkbox"
                    checked={createInvoiceEntryOnReceipt}
                    onChange={(e) => setCreateInvoiceEntryOnReceipt(e.target.checked)}
                  />
                  <label htmlFor="switch-create-invoice-entry"></label>
                </div>
              </div>
            </div>
          </div>
        }
        type="info"
        confirmText="Confirmar Recebimento"
        cancelText="Cancelar"
        onClose={() => {
          setShowReceiptModal(null);
          setCreateInvoiceEntryOnReceipt(true);
        }}
        onConfirm={() => {
          if (showReceiptModal) {
            performStageUpdate(showReceiptModal, 'transport', createInvoiceEntryOnReceipt);
          }
        }}
      />

      <Toast
        message={toast.message}
        type={toast.type}
        show={toast.show}
        onClose={hideToast}
        duration={4000}
      />
    </div>
  );
};

export default FollowUpPage;
